import { Box, Button, Stack, Tooltip } from '@mui/material'
import { observer } from 'mobx-react'
import moment from 'moment/moment'
import React, { FC } from 'react'
import { useIntl } from 'react-intl'
import DataUnit from '../../../../../components/customElements/DataUnit/DataUnit'
import UploadedPhotos from '../../../../../components/customElements/RequestDataAccordions/UploadedPhotos'
import EquipmentCheckingField from '../../../../../components/FormElements/EquipmentCheckingField'
import FieldError from '../../../../../components/FormElements/FieldError'
import FilterDatePicker from '../../../../../components/FormElements/FilterDatePicker/FilterDatePicker'
import FormReceiverAutocomplete from '../../../../../components/FormElements/FormReceiverAutocomplete'
import { getImageSrc } from '../../../../../helpers/getImageSrc'
import { formatSN } from '../../../../../utils/utils/formatSN'
import FormSelect from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormSelect'
import TypedAccordion from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/TypedAccordion'
import { RequestType } from '../../../../SingleRequestPage/types/Request.type'
import PhotosUpload from '../../utils/PhotosUpload'
import { useInstallationDataNew } from '../hooks/useInstallationDataNew'
interface InstallationDataAccNewProps {
  request: RequestType
  store: any
  photoPermission: any
}

const InstallationDataAccNew: FC<InstallationDataAccNewProps> = ({
  request,
  store,
  photoPermission,
}) => {
  const intl = useIntl()
  const {
    isValid,
    watch,
    isEquipmentIdChecked,
    form: {
      receiveresModels,
      canEditReceiver,
      onReset,
      isPending,
      isAtLeastOneChanged,
      isEditMode,
      dateError,
      canUploadImages,
      defaultValues,
      control,
      canEditEquipmentId,
      canEditData,
      setIsEditMode,
      onSubmit,
      showOnlyPhotos,
      errors,
      setValue,
      onCheckEquipment,
      lastCheckedId,
      setError,
    },
  } = useInstallationDataNew(request, store)

  const cantSubmitWithoutReceiver =
    request.receiverSerialNumber && request.receiverModel

  const newIsValid = cantSubmitWithoutReceiver
    ? isValid
    : !errors.installationDate && !errors.equipmentId && isEquipmentIdChecked

  return (
    <TypedAccordion
      defaultOpenState={[5, 6].includes(request.status)}
      summary={'Данные установки'}
      onEdit={canEditData ? () => setIsEditMode((prev) => !prev) : undefined}
    >
      <Stack component={'form'}>
        {isEditMode && (
          <Stack spacing={'16px'} mt={2}>
            {canEditEquipmentId ? (
              <EquipmentCheckingField
                control={control}
                defaultEquipmentId={request?.equipmentId ?? ''}
                equipmentId={watch('equipmentId')!}
                lastCheckedId={lastCheckedId ?? ''}
                onChecked={onCheckEquipment}
                error={!!errors.equipmentId}
                withLabel
              />
            ) : (
              <DataUnit name='ID оборудования' value={request.equipmentId} />
            )}
            {canEditReceiver ? (
              <>
                {watch('hasCas') && (
                  <DataUnit
                    name='S/N оборудования'
                    value={formatSN(watch('receiverSerialNumber'))}
                  />
                )}
                {!watch('hasCas') && (
                  <FormReceiverAutocomplete
                    control={control}
                    name='receiverSerialNumber'
                    fullWidth
                    label='S/N оборудования'
                    setValue={setValue as any}
                    modelName='receiverModelId'
                    setError={setError as any}
                  />
                )}
              </>
            ) : (
              <DataUnit
                name='S/N оборудования'
                value={formatSN(request.receiverSerialNumber)}
              />
            )}
            {watch('smartCardId') && (
              <FieldError msg='К данному серийному номеру привязана другая smart карта. Проверьте данные или обратитесь к администратору!' />
            )}
            {canEditReceiver ? (
              <>
                {watch('hasCas') && (
                  <DataUnit
                    name='Модель оборудования'
                    value={watch('receiverModelId')}
                  />
                )}
                {!watch('hasCas') && (
                  <FormSelect
                    control={control}
                    fullWidth
                    name='receiverModelId'
                    label='Модель оборудования'
                    placeholder=''
                    items={
                      receiveresModels.map(
                        (el: any) => `${el.manufacturerName} - ${el.name}`,
                      ) ?? []
                    }
                    withLabel
                    disabled
                  />
                )}
              </>
            ) : (
              <DataUnit
                name='Модель оборудования'
                value={request.receiverProduct ?? '-'}
              />
            )}
            <Stack direction={'row'} spacing={'10px'}>
              {dateError && (
                <Tooltip
                  title={
                    'Дату установки можно отредактировать только в рамках отчётного периода текущего отчёта'
                  }
                >
                  <img
                    src={getImageSrc('tooltip')}
                    alt={''}
                    style={{
                      width: '18px',
                      marginTop: 0,
                      marginBottom: '-4px',
                    }}
                  />
                </Tooltip>
              )}
              <FilterDatePicker
                control={control}
                name={'installationDate'}
                label='Дата установки'
                defaultValue={defaultValues.installationDate}
              />
            </Stack>
            {canUploadImages ? (
              // @ts-ignore
              <PhotosUpload
                store={store}
                title={intl.formatMessage(
                  {
                    id: `Загрузите от {minimumLimit} до {maximumLimit} фотографий`,
                    defaultMessage: `Загрузите от {minimumLimit} до {maximumLimit} фотографий`,
                  },
                  {
                    minimumLimit: photoPermission?.minLimitPerLoad,
                    maximumLimit: photoPermission?.maxLimitPerLoad,
                  },
                )}
              />
            ) : (
              <UploadedPhotos store={store} reloadCallback={() => {}} />
            )}
          </Stack>
        )}
        {!isEditMode && (
          <InstallationStaticData
            store={store}
            request={request}
            showOnlyPhotos={showOnlyPhotos}
          />
        )}
        {request.status !== 5 && isEditMode && (
          <>
            <Box className={'jst-sa'} sx={{ gap: '8px' }}>
              <Button
                type='button'
                disabled={!newIsValid || !isAtLeastOneChanged || dateError}
                color={'primary'}
                variant={'contained'}
                fullWidth
                loading={isPending}
                onClick={() => onSubmit(watch())}
              >
                {intl.formatMessage({
                  id: 'Применить изменения',
                })}
              </Button>
              <Button
                onClick={onReset}
                color={'primary'}
                variant={'contained'}
                fullWidth
              >
                {intl.formatMessage({ id: 'Отмена' })}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </TypedAccordion>
  )
}

interface InstallationStaticDataProps {
  showOnlyPhotos: boolean
  request: RequestType
  store?: any
}

const InstallationStaticData: FC<InstallationStaticDataProps> = ({
  showOnlyPhotos,
  request,
  store,
}) => {
  return (
    <>
      {!showOnlyPhotos && (
        <>
          <Box mt={2}>
            <DataUnit name='ID оборудования' value={request.equipmentId} />
          </Box>
          <Box mt={2}>
            <DataUnit
              name='Дата установки'
              value={
                request.installationDate
                  ? moment(request.installationDate).format('DD-MM-YYYY')
                  : '-'
              }
            />
          </Box>
          <Box mt={2}>
            <DataUnit
              name='S/N оборудования'
              value={formatSN(request.receiverSerialNumber)}
            />
          </Box>
          <Box mt={2}>
            <DataUnit
              name='Модель оборудования'
              value={request.receiverProduct ? request.receiverProduct : '-'}
            />
          </Box>
        </>
      )}
      <UploadedPhotos store={store} reloadCallback={() => {}} />
    </>
  )
}

export default observer(InstallationDataAccNew)
